/**
 * <custom-video-carousel>
 * - Loads each video only when it comes near the screen, then plays it silently on repeat.
 * - Pauses videos that leave the screen.
 * - Sound button turns sound on for one video and mutes the others.
 * - Arrows scroll the row on desktop.
 */
class CustomVideoCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.videos = Array.from(this.querySelectorAll('video[data-src]'));

    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.prev?.addEventListener('click', () => this.scrollByCard(-1), { signal });
    this.next?.addEventListener('click', () => this.scrollByCard(1), { signal });
    this.track.addEventListener('scroll', () => this.updateArrows(), { signal, passive: true });
    window.addEventListener('resize', () => this.updateArrows(), { signal, passive: true });
    this.updateArrows();

    this.querySelectorAll('[data-sound]').forEach((button) => {
      button.addEventListener('click', () => this.toggleSound(button), { signal });
    });

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!('IntersectionObserver' in window)) {
      this.videos.forEach((video) => this.load(video));
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            this.load(video);
            if (!reduceMotion) video.play().catch(() => {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0.35 }
    );
    this.videos.forEach((video) => this.observer.observe(video));
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  load(video) {
    if (video.dataset.loaded) return;
    video.src = video.dataset.src;
    video.dataset.loaded = 'true';
  }

  toggleSound(button) {
    const video = button.parentElement.querySelector('video');
    if (!video) return;
    const turnOn = video.muted;
    this.videos.forEach((other) => {
      other.muted = true;
    });
    this.querySelectorAll('[data-sound]').forEach((other) => {
      other.setAttribute('aria-pressed', 'false');
      other.setAttribute('aria-label', 'Turn sound on');
    });
    if (turnOn) {
      this.load(video);
      video.muted = false;
      video.play().catch(() => {});
      button.setAttribute('aria-pressed', 'true');
      button.setAttribute('aria-label', 'Turn sound off');
    }
  }

  scrollByCard(direction) {
    const card = this.track.querySelector('li');
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
    this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  updateArrows() {
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max;
  }
}

if (!customElements.get('custom-video-carousel')) {
  customElements.define('custom-video-carousel', CustomVideoCarousel);
}
